import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import bowser from 'yola-bowser';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash.get';
import isEqual from 'lodash.isequal';
import {
  site,
  dialogs,
  integration,
  assets,
  anodum,
  i18next,
  saving,
  siteLocales,
  view,
  customHtml,
} from '@yola/ws-sdk';
import { reactHookForm } from '@yola/ws-ui';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import { ViewportConsumer } from 'yola-editor/src/js/utils/viewport-provider';
import helpCenter from 'yola-editor/src/js/modules/help-center';
import yolaSite from 'yola-editor/src/js/modules/site';
import segment from 'src/js/modules/analytics/segment';
import { saveSiteWithEffect } from 'src/js/modules/saving/helpers/save-site';
import useCategoryLoadingBySlug from 'src/js/modules/ai/hooks/use-category-loading-by-slug';
import user from 'yola-editor/src/js/modules/user';
import publishingConstants from 'yola-editor/src/js/modules/publishing/constants';
import yolaDialogs from 'yola-editor/src/js/modules/dialogs';
import useAsyncSetState from '../hooks/use-async-set-state';
import scrollToElementInContainer from '../../scroller/helpers/scroll-to-element-in-container';
import scrollToElement from '../../scroller/helpers/scroll-to-element';
import dialogTypes from '../../dialogs/constants/dialog-types';
import ai from '../../ai';
import constants from '../constants';
import WebsiteSettings from '../components/website-settings';
import WebsiteSettingsContext from '../context/website-settings-context';
import checkIfHostingIsSyndicated from '../helpers/check-if-hosting-is-syndicated';
import getCookiebotInstructionsUrl from '../helpers/get-cookiebot-instructions-url';
import getWebsiteSettingsDefaultValues from '../helpers/get-website-settings-default-values';
import getSettingValueFromFieldValue from '../helpers/get-setting-value-from-field-value';
import trackers from '../trackers';
import pageSettingsTabIdentifiers from '../../page-settings/constants/tab-identifiers';
import addIdsToCustomCodeSnippets from '../../common/helpers/add-ids-to-custom-code-snippets';
import removeIdsFromCustomCodeSnippets from '../../common/helpers/remove-ids-from-custom-code-snippets';

const { useForm } = reactHookForm;

const {
  common,
  webSettingsConfig,
  settingTypes,
  externalLinks,
  integrationTypes,
  tabIdentifiers,
  settingLengthLimits,
} = constants;
const { fieldNames: customCodeFieldNames } = customHtml.constants;
const {
  GOOGLE_ANALYTICS_INSTRUCTIONS_URL,
  GOOGLE_SEARCH_CONSOLE_INSTRUCTIONS_URL,
  GOOGLE_OWNERSHIP_VERIFICATION_URL,
} = externalLinks;
const {
  trackers: { trackDialogActiveTabChanged },
} = segment;

const customCodeFieldNamesList = Object.values(customCodeFieldNames);

const {
  trackWebsiteSettingsDialogCanceled,
  trackWebsiteSettingsDialogDisplayed,
  trackWebsiteSettingsDialogSubmitted,
  trackAiTutorialClicked,
  trackBusinessCategorySelected,
  trackBusinessCategoryInputFocused,
} = trackers;
const {
  constants: {
    siteProperties: { UNSPLASH_SEARCH_QUERY_PROPERTY_NAME },
  },
  helpers: { getUnsplashSearchQuery },
} = ai;

const MIN_WIDTH = 568;
const MAX_WIDTH = 800;
const MIN_HEIGHT = 600;
const MAX_HEIGHT = 720;

const getCaptions = (data = {}) => ({
  prompt: {
    title: i18next.t('Save changes?'),
    description: i18next.t('Do you want to save changes you made in website settings?'),
    discard: i18next.t('Discard'),
    submit: i18next.t('Save'),
    cancel: i18next.t('Cancel'),
  },
  websiteSettings: i18next.t('Website settings'),
  businessInfo: {
    errorMessages: {
      required: i18next.t('This field needs your input'),
      businessCategory: i18next.t('Please select a valid category'),
      businessDescription: {
        minLength: i18next.t(`Please provide a description with {number}+ characters`, {
          number: settingLengthLimits.MIN_BUSINESS_DESCRIPTION_CHARS,
        }),
        maxLength: i18next.t(`Maximum number of characters {number}`, {
          number: settingLengthLimits.MAX_BUSINESS_DESCRIPTION_CHARS,
        }),
      },
      phone: i18next.t('Invalid phone number. Please, check the phone number and try again'),
      email: i18next.t('Invalid email. Please, check the email and try again'),
    },
    profileSection: {
      title: i18next.t('Profile'),
    },
    contactInfoSection: {
      title: i18next.t('Contact Info'),
      description: i18next.t('Add your contact details so clients can reach you easily.'),
    },
    businessName: {
      name: i18next.t('Business name'),
      checkboxLabel: i18next.t('Same as website name'),
    },
    businessCategory: {
      title: i18next.t('Business category'),
      placeholder: i18next.t('E.g. Cleaning Company'),
      noResultsText: i18next.t('No result'),
    },
    businessDescription: {
      name: i18next.t('Business description'),
      placeholder: i18next.t(
        'Alex & Johnson - is a real estate investment and development company based in Georgia. We specialize in acquiring, managing, and maximizing the value of residential properties.'
      ),
      description: i18next.t(
        'Describe your business or project in a sentence or two, it will help visitors to better understand what you do'
      ),
    },
    address: {
      title: i18next.t('Address'),
      placeholder: i18next.t('e.g. 1st Example st., Example City, Example Country'),
    },
    phone: {
      title: i18next.t('Phone'),
      placeholder: i18next.t('e.g. +1 234 567 8910'),
    },
    email: {
      title: i18next.t('Email'),
      placeholder: i18next.t('e.g. example@gmail.com'),
    },
    businessHours: {
      title: i18next.t('Working hours'),
      placeholder: i18next.t('e.g. Sun: Closed, Mon-Fri: 8 am-6 pm, Sat: 10 am-4 pm'),
    },
    aiBanner: {
      title: i18next.t('Boost your business with AI!'),
      description: i18next.t(
        'Explore our AI-powered tools to complete your website in under 5 minutes.'
      ),
      buttonLabel: i18next.t('Learn more'),
    },
  },
  tabs: {
    general: i18next.t('General'),
    businessInfo: i18next.t('Business Info'),
    integrations: i18next.t('Integrations'),
    customCode: i18next.t('Custom code'),
    multilingual: i18next.t('Multilingual'),
    hosting: i18next.t('Hosting'),
    siteBackups: i18next.t('Backups'),
    domain: i18next.t('Domain'),
  },
  apply: i18next.t('Submit'),
  cancel: i18next.t('Cancel'),
  siteName: {
    name: i18next.t('Website name'),
    description: data.isWL
      ? i18next.t(
          'The name of your web project is not used to populate website content. To manage your website title, go to <a href>Page Settings → SEO</a> of your Home page'
        )
      : i18next.t(
          "Your website project name shows on My Sites page in your Dashboard. It's not used to populate website content. To manage your website title, go to <a href>Page Settings → SEO</a> of your Home page"
        ),
    errorMessages: {
      required: i18next.t('Field is required'),
    },
  },
  businessLogo: {
    name: i18next.t('Site logo'),
    description: i18next.t(
      "Upload your business logo. We'll ensure it's updated throughout your website."
    ),
    preview: i18next.t('Preview logo'),
  },
  favicon: {
    name: i18next.t('Favicon'),
    description: i18next.t(
      "Add your own branding to site visitors' bookmarks and browser tabs by including a favicon."
    ),
  },
  socialLinks: {
    name: i18next.t('Social profiles'),
    button: i18next.t('Add social profile'),
    validationError: i18next.t('Invalid link'),
  },
  multilingual: {
    title: i18next.t('Website languages'),
    description: i18next.t(
      'Add multiple languages to your site so visitors can browse in their language'
    ),
    addButton: i18next.t('Add language'),
    mainLang: i18next.t('Main language'),
    setMainLang: i18next.t('Set as main language'),
    deleteLang: i18next.t('Delete language'),
    banner: {
      title: i18next.t('Generate content in your preferred language'),
      description: i18next.t(
        'Set your preferred language for AI-generated content in you language settings'
      ),
      buttonLabel: i18next.t('Learn more'),
    },
  },
  customCode: {
    name: i18next.t('Custom code'),
    description: i18next.t(
      'Tweak the site by adding custom code, and integration snippets. The code added here will apply to all pages of your site.'
    ),
  },
  cookiesUserData: {
    name: i18next.t('Cookies & User data'),
    title: i18next.t('Cookie consent banner'),
    description: i18next.t(
      'Show your visitors a banner informing them about the use of cookies on your website'
    ),
  },
  fileInputCaptions: {
    button: i18next.t('Choose file'),
    empty: i18next.t('No file chosen'),
    fileItemName: i18next.t('files'),
    wrongFileType: i18next.t('Wrong file type'),
  },
  exportSite: {
    name: i18next.t('Site export'),
    description: i18next.t(
      'Export the HTML, CSS, assets, and JavaScript of your site from inside the {productName}',
      {
        productName: data.productName,
      }
    ),
    button: i18next.t('Prepare ZIP'),
  },
  dangerZoneCaptions: {
    name: i18next.t('Danger zone'),
    description: i18next.t(
      'Changes in this area can negatively affect your website performance. Please proceed with care.'
    ),
    preventGoogleSearchIndexing: {
      name: i18next.t('Hide this website from search results'),
      description: i18next.t(
        'Prevent your website from being indexed by search engines to hide it in Google search'
      ),
      label: i18next.t('Hide this website'),
    },
    unpublish: {
      name: i18next.t('Unpublish site'),
      description: i18next.t(
        "Unpublish your site if you don't want it to remain online anymore. You will continue to own your domain and you will be able to publish another website using this domain name."
      ),
      button: i18next.t('Unpublish site'),
    },
    reset: {
      name: i18next.t('Reset site'),
      description: i18next.t(
        'Reset your website if you want to start using another template. Please proceed with this step carefully. You cannot recover your site after the reset.'
      ),
      button: i18next.t('Reset site'),
    },
    switchTemplate: {
      name: i18next.t('Switch template'),
      description: i18next.t(
        'Switch website template if you want to start using another one. All text content and images will be saved.'
      ),
      button: i18next.t('Switch template'),
    },
  },
  integrations: {
    description: i18next.t(
      'Easily add third-party integrations like Google Analytics and Webmaster tools to your website'
    ),
    googleAnalytics: {
      name: i18next.t('Google Analytics'),
      description: i18next.t(
        'Discover where your visitors come from and what they do on your site'
      ),
      about: i18next.t(
        "With Google Analytics, you can track your site's traffic and gain a deeper understanding of who's visiting your site. You can see information like the number of visitors to a page, where visitors come from, and how long they stay."
      ),
      step1: {
        title: i18next.t('Set up a Google Analytics Property ID'),
        description: i18next.t(
          `Follow <a target="_blank" href="{googleInstructionsUrl}">Google’s instructions</a> to set up a property ID.`,
          { googleInstructionsUrl: GOOGLE_ANALYTICS_INSTRUCTIONS_URL }
        ),
      },
      step2: {
        title: i18next.t('Add your Google Analytics Tracking ID to your site'),
        description: i18next.t('Google Analytics Tracking ID'),
      },
      control: {
        name: i18next.t('Google Analytics Tracking ID'),
        description: i18next.t(
          'Use the property Tracking ID from the property you have created in Google analytics'
        ),
        validationError: i18next.t('Invalid Google Analytics Tracking ID'),
      },
      notes: [
        i18next.t(
          'If you’ve pasted your Google Analytics into any of your site’s custom code areas, be sure to remove it to prevent inaccurate data.'
        ),
        i18next.t('Don’t forget to save and publish your site for changes to take effect'),
      ],
      disconnectAlert: i18next.t(
        `You’re about to disconnect the {integrationType} integration from your site. Are your sure?`,
        { integrationType: i18next.t('Google Analytics') }
      ),
    },
    googleSearchConsole: {
      name: i18next.t('Google Search Console'),
      description: i18next.t(
        'Verify and claim ownership of your site to get access to your performance statistics on Google Search'
      ),
      about: i18next.t(
        'With Google Search Console you can track and understand how you are performing on Google Search and what you can do to improve your appearance on search to bring more relevant traffic to your website.'
      ),
      step1: {
        title: i18next.t('Set up a Search Console property'),
        description: i18next.t(
          `Follow <a target="_blank" href="{googleInstructionsUrl}">Google’s instructions</a> to set up a property. Choose the “HTML tag” method on the <a target="_blank" href="{ownershipVerificationUrl}">Ownership verification page</a> for your property.`,
          {
            googleInstructionsUrl: GOOGLE_SEARCH_CONSOLE_INSTRUCTIONS_URL,
            ownershipVerificationUrl: GOOGLE_OWNERSHIP_VERIFICATION_URL,
          }
        ),
      },
      step2: {
        title: i18next.t('Add the verification tag from the Search Console to your site'),
        description: i18next.t('HTML tag'),
      },
      control: {
        name: i18next.t('HTML tag'),
        description: i18next.t(
          `Use the verification HTML tag you have recived on the <a target="_blank" href="{ownershipVerificationUrl}">Ownership verification page</a> of your property in Google Search Console`,
          {
            ownershipVerificationUrl: GOOGLE_OWNERSHIP_VERIFICATION_URL,
          }
        ),
        validationError: i18next.t('Invalid verification HTML tag'),
      },
      notes: [i18next.t('Don’t forget to save and publish your site for changes to take effect')],
      disconnectAlert: i18next.t(
        `You’re about to disconnect the {integrationType} integration from your site. Are your sure?`,
        { integrationType: i18next.t('Google Search Console') }
      ),
    },
    cookiebot: {
      name: i18next.t('Cookiebot by Usercentrics'),
      description: {
        connected: i18next.t(
          'You’re currently using Cookiebot CMP to manage user cookies. Disconnect it if you want to switch back to a basic cookie banner.'
        ),
        disconnected: i18next.t(
          "Don't risk non-compliance with key privacy laws GDPR and CCPA. Easily add the automated Cookiebot CMP now"
        ),
      },
      about: i18next.t(
        'Cookiebot Consent Management Platform (CMP) helps make your use of cookies and online tracking compliant with GDPR and CCPA.'
      ),
      step1: {
        title: i18next.t('Create a Cookiebot CMP account and add your domain'),
        description: i18next.t(
          'Sign up to create an account. Log in to your account and add your website domain name(s). The website will now be scanned (please allow up to 24 hours for the scan to complete). Please note: your website has to be already published for Cookiebot CMP to successfuly scan your website.'
        ),
        btnLabel: i18next.t('Create account'),
      },
      step2: {
        title: i18next.t('Configure the cookie consent widget you want to display on your website'),
        description: i18next.t(
          `Follow the <a target="_blank" href="{instructionsUrl}">instructions</a> to configure and customize your cookie consent widget`,
          {
            instructionsUrl: getCookiebotInstructionsUrl(),
          }
        ),
      },
      step3: {
        title: i18next.t('Copy and paste the Domain Group ID from the CookieBot account:'),
        description: i18next.t(
          `Follow the <a target="_blank" href="{instructionsUrl}">instructions</a> to find and copy the Domain Group ID`,
          {
            instructionsUrl: getCookiebotInstructionsUrl(),
          }
        ),
      },
      control: {
        name: i18next.t('Domain Group ID'),
      },
      notes: [
        i18next.t(
          "Connecting this integration will automatically disable the display of Yola's basic Cookie banner on your website"
        ),
        i18next.t(
          'It might take up to 24 hours for the scan to get finished completely after you add the domain name(s)'
        ),
        i18next.t('Don’t forget to save and publish your site for changes to take effect'),
      ],
      disconnectAlert: i18next.t(
        `You’re about to disconnect the {integrationType} integration from your site. Are your sure?`,
        { integrationType: i18next.t('Cookiebot by Usercentrics') }
      ),
      afterDisconnectInfo: i18next.t(
        'Please note that you also need to manually remove the domain from your Cookiebot account or close your account entirely.'
      ),
    },
  },
});

const getWrongFileDialogText = () => ({
  favicon: {
    title: i18next.t('Unsupported file type'),
    description: i18next.t(
      'Please ensure your file is in one of the following formats: .ico, .png'
    ),
  },
  businessLogo: {
    title: i18next.t('Unsupported file type'),
    description: i18next.t(
      'Please ensure your file is in one of the following formats: .img, .jpeg, .jpg, .png, .gif, .svg'
    ),
  },
});

const defaultForm = {};

function WebsiteSettingsContainer({
  onAfterDialogClose = Function.prototype,
  onDialogMainAction,
  onDialogCancel,
  formData = null,
  form = defaultForm,
  showPromptForce = false,
  settingToScroll = '',
}) {
  const [featureFlags] = useFeatureFlags([
    'site_export',
    'business_info_in_website_settings',
    'ai_site_wizard',
    'ai_page_wizard',
    'ai_block_generator',
    'ai_text_assistant_rewrite',
    'ai_text_assistant_write',
    'ai_unsplash_search_query',
  ]);

  const {
    websiteSettings,
    redirects,
    integrations,
    dialog,
    integrationSettings,
    savingStatus,
    siteId,
    productName,
    userData,
    homePageId,
    publishedSiteData,
    publishingType,
    isWL,
  } = useSelector((state) => {
    const intSettings = integration.selectors.getSettings(state);
    const mainLocale = siteLocales.selectors.getMainLocale(state);
    const settings = site.selectors.getSettings(state);
    const { googleAnalyticsId, googleWebmasterCode, ...otherSettings } = settings;

    return {
      websiteSettings: getWebsiteSettingsDefaultValues(otherSettings),
      redirects: site.selectors.getRedirects(state),
      integrations: {
        googleAnalyticsId,
        googleWebmasterCode,
        ...site.selectors.getIntegrations(state),
      },
      dialog: dialogs.selectors.getDialog(state),
      integrationSettings: intSettings,
      savingStatus: saving.selectors.getStatus(state),
      siteId: site.selectors.getSiteId(state),
      productName: get(intSettings, 'branding.productName'),
      userData: integration.selectors.getUserData(state),
      homePageId: site.selectors.getHomePage(state, mainLocale).id,
      publishedSiteData: yolaSite.selectors.getPublishedSiteData(state),
      isWL: user.selectors.getWhiteLabelStatus(state),
      publishingType: user.selectors.getPublishingType(state),
    };
  });

  const hasSiteToUnpublish =
    publishedSiteData &&
    (!isWL || publishingType === publishingConstants.YOLA_PARTNER_PUBLISHING_TYPE);

  const initialValues = useMemo(() => {
    if (formData) {
      return formData;
    }

    return {
      ...websiteSettings,
      [settingTypes.BUSINESS_NAME]:
        websiteSettings[settingTypes.BUSINESS_NAME] || websiteSettings[settingTypes.SITE_NAME],
      ...Object.fromEntries(
        customCodeFieldNamesList.map((type) => [
          [type],
          addIdsToCustomCodeSnippets(websiteSettings[type]),
        ])
      ),
      ...integrations,
      redirects,
      ...form,
    };
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const defaultFormValues = useMemo(
    () => ({
      ...initialValues,
      [settingTypes.BUSINESS_CATEGORY]: initialValues[settingTypes.BUSINESS_CATEGORY]
        ? { value: initialValues[settingTypes.BUSINESS_CATEGORY] }
        : initialValues[settingTypes.BUSINESS_CATEGORY],
    }),
    [initialValues]
  );

  const [isBusinessNameSameAsSiteName, setIsBusinessNameSameAsSiteName] = useState(
    () => initialValues[settingTypes.BUSINESS_NAME] === initialValues[settingTypes.SITE_NAME]
  );

  const {
    setValue,
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: defaultFormValues,
    mode: 'onTouched',
  });

  const values = watch();

  /**
   * We need to clean up the form values to match the format of the initial values.
   * We need this for the correct display of the save prompt dialog.
   * */
  const formValues = useMemo(
    () =>
      Object.entries(values).reduce((accum, [key, value]) => {
        const acc = accum;

        if (key in initialValues) {
          if (key === settingTypes.BUSINESS_CATEGORY) {
            acc[key] = getSettingValueFromFieldValue(key, value);
          } else {
            acc[key] = value;
          }
        } else if (value === undefined) {
          return acc;
        }

        return acc;
      }, {}),
    [initialValues, values]
  );

  const isBusinessCategoryReady = useCategoryLoadingBySlug(
    websiteSettings[settingTypes.BUSINESS_CATEGORY],
    (data) => {
      setValue(settingTypes.BUSINESS_CATEGORY, {
        label: data.title,
        value: data.category,
        archetype: data.archetypes[0],
      });
    }
  );

  useEffect(() => {
    trackWebsiteSettingsDialogDisplayed(initialValues);
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const dispatch = useDispatch();

  const onUpdateSettings = useCallback(
    (options) => {
      dispatch(site.actions.updateSettings(options));
    },
    [dispatch]
  );

  const onBusinessCategoryFocus = useCallback(() => {
    trackBusinessCategoryInputFocused({
      category: values[settingTypes.BUSINESS_CATEGORY]?.value || null,
    });
  }, [values]);

  const onBusinessCategoryChange = useCallback(
    (option) => {
      setValue(settingTypes.BUSINESS_CATEGORY, option || null);

      if (isBusinessCategoryReady && option) {
        trackBusinessCategorySelected({
          category: option.value,
          archetype: option.archetype,
        });
      }
    },
    [setValue, isBusinessCategoryReady]
  );

  const onUpdateIntegrations = useCallback(
    (updatedIntegrations) => {
      dispatch(site.actions.updateIntegrations(updatedIntegrations));
    },
    [dispatch]
  );

  const onUpdateRedirects = useCallback(
    (updatedRedirects) => {
      dispatch(site.actions.updateRedirects(updatedRedirects));
    },
    [dispatch]
  );

  const hideDialog = useCallback(() => {
    dispatch(dialogs.actions.hide());
  }, [dispatch]);

  const onUnpublish = useCallback(() => {
    dispatch(yolaDialogs.actions.show(yolaDialogs.dialogTypes.UNPUBLISH_CONFIRM_ACTION));
  }, [dispatch]);

  const onWrongTypeFile = useCallback(
    (file, formDataReference, type) => {
      const wrongFileDialogText = getWrongFileDialogText();

      dispatch(
        dialogs.actions.show(dialogTypes.FILE_ERROR_DIALOG, {
          captions: {
            ...wrongFileDialogText[type],
            dismiss: i18next.t('Cancel'),
          },
          glyph: 'alert-circle',
          onDismiss: () => {
            dispatch(
              dialogs.actions.show(dialogTypes.WEBSITE_SETTINGS, { form: formDataReference })
            );
          },
        })
      );
    },
    [dispatch]
  );

  const onLoadProgressDialog = useCallback(() => {
    dispatch(
      dialogs.actions.show(dialogTypes.UPLOAD_PROGRESS_DIALOG, {
        overlay: 'visible',
        cancelable: false,
        progressType: 'indeterminate',
        captions: {
          title: `${i18next.t('Uploading image')}...`,
          description: i18next.t('Your image will be uploaded to the website soon'),
          cancel: i18next.t('Cancel'),
        },
      })
    );
  }, [dispatch]);

  const onUnsavedActivePageChange = useCallback(
    (callback) => {
      dispatch(
        dialogs.actions.show(dialogTypes.UNSAVED_WARNING, {
          callback,
          captions: { title: i18next.t('Your site has unsaved changes') },
        })
      );
    },
    [dispatch]
  );

  const onExportSite = useCallback(
    (siteIdToExport) => {
      const onSuccess = (data) => {
        const { url: siteArchiveLink } = data;

        dispatch(
          dialogs.actions.show(dialogTypes.EXPORT_INFORMATION_DIALOG, {
            siteArchiveLink,
            onDismiss() {
              dispatch(dialogs.actions.hide());
            },
          })
        );
      };

      const onFail = () => {
        dispatch(dialogs.actions.show(dialogTypes.EXPORT_ERROR_DIALOG));
      };

      dispatch(dialogs.actions.show(dialogTypes.EXPORT_PROGRESS_DIALOG));

      dispatch(
        site.actions.exportSite(siteIdToExport, {
          includeMeta: true,
          onSuccess,
          onFail,
        })
      );
    },
    [dispatch]
  );

  const setSiteProperty = useCallback(
    (property, value) => {
      dispatch(site.actions.setSiteProperty(property, value));
    },
    [dispatch]
  );

  const getSettingsToUpdate = useCallback(
    (updatedFormData) =>
      webSettingsConfig.reduce((accum, config) => {
        const { name } = config;
        let value = updatedFormData[name];

        if (typeof value === 'string') {
          value = value.trim();
        }

        if (name === settingTypes.BUSINESS_CATEGORY) {
          const categoryValue = getSettingValueFromFieldValue(name, value);

          if (websiteSettings[name] !== categoryValue) {
            return {
              [name]: categoryValue,
              ...accum,
            };
          }

          return accum;
        }

        if (customCodeFieldNamesList.includes(name)) {
          const snippets = removeIdsFromCustomCodeSnippets(
            getSettingValueFromFieldValue(name, value)
          );

          if (!isEqual(websiteSettings[name], snippets)) {
            return {
              [name]: snippets,
              ...accum,
            };
          }

          return accum;
        }

        if (websiteSettings[name] !== value) {
          return {
            [name]: getSettingValueFromFieldValue(name, value),
            ...accum,
          };
        }

        return accum;
      }, {}),
    [websiteSettings]
  );

  const getIntegrationsToUpdate = useCallback(
    (updatedFormData) =>
      Object.values(integrationTypes).reduce((accum, key) => {
        const value = updatedFormData[key];

        if (integrations[key] !== value) {
          return {
            [key]: value,
            ...accum,
          };
        }

        return accum;
      }, {}),
    [integrations]
  );

  const onClose = useCallback(() => {
    hideDialog();
    onAfterDialogClose();
  }, [hideDialog, onAfterDialogClose]);

  const onSubmit = useCallback(
    async (updatedFormData) => {
      const { ai_unsplash_search_query: isAiUnsplashSearchQueryEnabled } = featureFlags;

      const settingsForUpdate = getSettingsToUpdate(updatedFormData);
      const integrationsForUpdate = getIntegrationsToUpdate(updatedFormData);

      // Set native cookie banner to false, if cookiebot is connected
      if (integrationsForUpdate.cookiebotId && websiteSettings.isCookieBannerEnabled) {
        settingsForUpdate.isCookieBannerEnabled = false;
      }

      const isSettingsForUpdate = !!Object.keys(settingsForUpdate).length;
      const isRedirectsForUpdate = !isEqual(updatedFormData.redirects, redirects);
      const isIntegrationsForUpdate = !!Object.keys(integrationsForUpdate).length;
      const isCustomCodeSettingsForUpdate = customCodeFieldNamesList.some((type) =>
        Object.keys(settingsForUpdate).includes(type)
      );

      trackWebsiteSettingsDialogSubmitted({
        onDialogMainAction,
        initialValues,
        settingsForUpdate,
        integrationsForUpdate,
      });

      if (!isSettingsForUpdate && !isRedirectsForUpdate && !isIntegrationsForUpdate) {
        onClose();
        return;
      }

      if (isSettingsForUpdate) {
        onUpdateSettings(settingsForUpdate);
      }
      if (isRedirectsForUpdate) {
        onUpdateRedirects(updatedFormData.redirects);
      }
      if (isIntegrationsForUpdate) {
        onUpdateIntegrations(integrationsForUpdate);
      }
      if (isCustomCodeSettingsForUpdate) {
        dispatch(view.actions.forceReloadView());
      }

      onClose();

      saveSiteWithEffect(async () => {
        if (
          isAiUnsplashSearchQueryEnabled &&
          settingsForUpdate[settingTypes.BUSINESS_DESCRIPTION]
        ) {
          const searchQuery = await getUnsplashSearchQuery();
          if (searchQuery) {
            setSiteProperty(UNSPLASH_SEARCH_QUERY_PROPERTY_NAME, searchQuery);
          }
        }
      });
    },
    [
      featureFlags,
      getIntegrationsToUpdate,
      getSettingsToUpdate,
      initialValues,
      onClose,
      onDialogMainAction,
      onUpdateIntegrations,
      onUpdateRedirects,
      onUpdateSettings,
      redirects,
      setSiteProperty,
      websiteSettings.isCookieBannerEnabled,
      dispatch,
    ]
  );

  const onCancel = useCallback(() => {
    trackWebsiteSettingsDialogCanceled({
      onDialogCancel,
    });

    onClose();
  }, [onDialogCancel, onClose]);

  const setImagePreview = useCallback((src) => {
    if (!src) return null;
    return site.helpers.composeAssetUrl(src);
  }, []);

  const [activeTabId, setActiveTabId] = useAsyncSetState(() => {
    const { modalProps } = dialog;
    return (modalProps && modalProps.activeTabId) || tabIdentifiers.GENERAL;
  });

  const onTabChange = useCallback(
    (tabId) => {
      setActiveTabId(tabId);
      trackDialogActiveTabChanged({ dialogId: dialogTypes.WEBSITE_SETTINGS, tabId });
    },
    [setActiveTabId]
  );

  const shouldSettingsBeUpdated = useCallback(
    (updatedFormData) => {
      const settingsForUpdate = getSettingsToUpdate(updatedFormData);
      const integrationsForUpdate = getIntegrationsToUpdate(updatedFormData);

      const isSettingsForUpdate = !!Object.keys(settingsForUpdate).length;
      const isRedirectsForUpdate = updatedFormData.redirects !== redirects;
      const isIntegrationsForUpdate = !!Object.keys(integrationsForUpdate).length;

      return isSettingsForUpdate || isRedirectsForUpdate || isIntegrationsForUpdate;
    },
    [getSettingsToUpdate, getIntegrationsToUpdate, redirects]
  );

  const backToWebsiteSettingsDialog = useCallback(
    (data) => {
      dispatch(
        dialogs.actions.show(dialogTypes.WEBSITE_SETTINGS, {
          formData: data.formData,
          activeTabId: data.activeTabId,
          showPromptForce: data.shouldShowPromptForce,
        })
      );
    },
    [dispatch]
  );

  const openPageSettingsSEOTab = useCallback(() => {
    dispatch(
      dialogs.actions.show(dialogTypes.PAGE_SETTINGS, {
        pageId: homePageId,
        activeTabId: pageSettingsTabIdentifiers.SEO,
      })
    );
  }, [dispatch, homePageId]);

  const settingsFormRef = useRef(null);

  const submitSettings = useCallback(() => {
    if (settingsFormRef.current) {
      settingsFormRef.current.submit();
    }
  }, []);

  const onSiteNameDescriptionClick = useCallback(
    (e) => {
      e.preventDefault();

      const captions = getCaptions({ productName, isWL });

      if (e.target.tagName === 'A') {
        if (Object.keys(errors).length) {
          submitSettings();
          return;
        }

        if (shouldSettingsBeUpdated(values)) {
          dispatch(
            dialogs.actions.show(dialogTypes.CONFIRM_DIALOG, {
              onCancel: () => openPageSettingsSEOTab(),
              onSubmit: () => {
                onSubmit(values, true);
                openPageSettingsSEOTab();
              },
              captions: captions.prompt,
            })
          );
          return;
        }

        openPageSettingsSEOTab();
      }
    },
    [
      productName,
      errors,
      shouldSettingsBeUpdated,
      values,
      openPageSettingsSEOTab,
      submitSettings,
      isWL,
      dispatch,
      onSubmit,
    ]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === common.ENTER_KEY) {
        if (anodum.isOneOfTags(e.target, ['input', 'textarea'])) return;

        submitSettings();
      }
    },
    [submitSettings]
  );

  const uploadImage = useCallback(
    (file, formDataReference, type) => {
      onLoadProgressDialog();

      return assets.operations.clientUploadAsset(file[0]).then(() => {
        assets.operations
          .createMediaGalleryAssets({
            asset: file[0],
            name: file[0].name,
          })
          .then((result) => {
            backToWebsiteSettingsDialog({
              formData: {
                ...formDataReference,
                [type]: result.data.source,
              },
              shouldShowPromptForce: true,
            });
          });
      });
    },
    [backToWebsiteSettingsDialog, onLoadProgressDialog]
  );

  const handleSwitchTemplate = useCallback(() => {
    const { UNSAVED } = saving.constants.statuses;

    if (savingStatus === UNSAVED) {
      onUnsavedActivePageChange(integrationSettings.onSwitchTemplate);
    } else {
      integrationSettings.onSwitchTemplate();
    }
  }, [savingStatus, onUnsavedActivePageChange, integrationSettings]);

  const showWebsiteSettingsDialog = useCallback(
    (data = {}) => {
      const updatedFormData = { ...formValues, ...data.formData };

      backToWebsiteSettingsDialog({
        formData: updatedFormData,
        activeTabId: data.activeTabId,
        shouldShowPromptForce: shouldSettingsBeUpdated(updatedFormData),
      });
    },
    [formValues, backToWebsiteSettingsDialog, shouldSettingsBeUpdated]
  );

  const {
    site_export: isSiteExportFeatureFlagEnabled,
    business_info_in_website_settings: isBusinessInfoEnabled,
    ai_site_wizard: isAiSiteWizardEnabled,
    ai_page_wizard: isAiPageWizardEnabled,
    ai_block_generator: isAiBlockGeneratorEnabled,
    ai_text_assistant_rewrite: isTextAssistantRewriteEnabled,
    ai_text_assistant_write: isTextAssistantWriteEnabled,
  } = featureFlags;

  const { employee_id: employeeId } = userData;
  const isDomainTabEnabled = !isWL;
  const captions = getCaptions({ productName, isWL });
  const areAiFeaturesAvailable = Boolean(
    isAiSiteWizardEnabled ||
    isAiPageWizardEnabled ||
    isAiBlockGeneratorEnabled ||
    isTextAssistantRewriteEnabled ||
    isTextAssistantWriteEnabled
  );
  const aiTutorialUrl = helpCenter.accessors.getAiTutorialUrl();

  const isSiteExportEnabled = isSiteExportFeatureFlagEnabled || Boolean(employeeId);

  const { modalProps } = dialog;
  const fieldNameForScroll = (modalProps && modalProps.field) || null;
  const isSyndicatedHosting = checkIfHostingIsSyndicated(
    integrationSettings.userData.publishing_type
  );

  return (
    <ViewportConsumer>
      {({ width, height }) => (
        <WebsiteSettingsContext.Provider
          value={{
            control,
            setFieldValue: setValue,
            formErrors: errors,
            formValues,
            initialValues,
            width: width >= 1440 ? MAX_WIDTH : MIN_WIDTH,
            height: height >= 800 ? MAX_HEIGHT : MIN_HEIGHT,
            mobile: Boolean(bowser.mobile || bowser.tablet),
            isMobile: Boolean(bowser.mobile),
            captions,
            onClose: hideDialog,
            onSubmit: handleSubmit(onSubmit),
            onCancel,
            scrollElementIntoView: scrollToElementInContainer,
            scrollToElement,
            activeTabId,
            isBusinessNameSameAsSiteName,
            setIsBusinessNameSameAsSiteName,
            fieldNameForScroll,
            onWrongTypeFile,
            uploadImage,
            integrationTypes,
            setImagePreview,
            onKeyDown: handleKeyDown,
            showPromptForce,
            onUnpublish: hasSiteToUnpublish ? onUnpublish : null,
            onResetSite: integrationSettings.onResetSite,
            onExportSite: () => onExportSite(siteId),
            onSwitchTemplate: handleSwitchTemplate,
            isSiteExportEnabled,
            isBusinessInfoEnabled,
            onTabChange,
            setActiveTabId,
            isSyndicatedHosting,
            isDomainTabEnabled,
            settingToScroll,
            onSiteNameDescriptionClick,
            areAiFeaturesAvailable,
            aiTutorialUrl,
            onBusinessCategoryFocus,
            onBusinessCategoryChange,
            onAiBannerClick: () => {
              trackAiTutorialClicked({
                triggerId: 'website-settings',
                tutorialId: 'learn-about-ai',
              });
            },
            showWebsiteSettingsDialog,
            onAfterDialogClose,
          }}
        >
          <WebsiteSettings ref={settingsFormRef} />
        </WebsiteSettingsContext.Provider>
      )}
    </ViewportConsumer>
  );
}

WebsiteSettingsContainer.propTypes = {
  formData: PropTypes.shape(),
  form: PropTypes.shape(),
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
  onAfterDialogClose: PropTypes.func,
  settingToScroll: PropTypes.oneOf([...Object.values(settingTypes), '']),
  showPromptForce: PropTypes.bool,
};

export default WebsiteSettingsContainer;
